import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { apiJson, isUnauthorizedError } from '../services/api';
import PageLoader from '../components/PageLoader';
import './StatementReview.css';
import './Documents.css';

const fmt = (n) =>
  n == null || n === '' ? '—' :
  (n < 0 ? '-' : '') + '$' + Math.abs(Number(n)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCat = (cat) => (cat || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const INCOME_CATS = ['rent', 'late_fee', 'pet_fee', 'parking', 'laundry', 'other'];
const EXPENSE_CATS = ['mortgage', 'insurance', 'property_tax', 'repair', 'maintenance', 'management', 'utilities', 'hoa', 'vacancy', 'capex', 'other', 'owner_distribution'];
const OWNER_DIST = 'owner_distribution';
const getRowKey = (row) => row?.id ?? row?._tempId ?? null;
const getStatementTableRowKey = (row) => row?._type && row?._id != null ? `${row._type}:${row._id}` : null;
const isSameRow = (left, right) => {
  const leftKey = getRowKey(left);
  const rightKey = getRowKey(right);
  return leftKey != null && rightKey != null && leftKey === rightKey;
};

function triggerDocumentDownload(url, filename = 'statement.pdf') {
  if (!url) return;
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.target = '_blank';
  a.rel = 'noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ── Reusable panel — used by both the standalone page and PropertyDetail ──────

export function StatementReviewPanel({ propertyId, embedded = false, externalToast = null }) {
  const navigate = useNavigate();
  const localToast = useRef(null);
  const toastRef = externalToast || localToast;

  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [allIncome, setAllIncome] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [ledgerExpanded, setLedgerExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [selectedTableRows, setSelectedTableRows] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [documentUrls, setDocumentUrls] = useState({});
  const [selectedDocLink, setSelectedDocLink] = useState({ docId: null, url: '', loading: false });
  const [newRow, setNewRow] = useState({
    row_type: 'income',
    date: '',
    category: INCOME_CATS[0],
    amount: null,
    vendor: '',
    description: '',
  });

  function showToast(msg, type = 'success') {
    toastRef.current?.show({
      severity: type,
      summary: type === 'error' ? 'Error' : type === 'warn' ? 'Warning' : 'Success',
      detail: msg,
      life: type === 'error' ? 5000 : 3200,
    });
  }

  const redirectToLogin = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prop, docs, inc, exp] = await Promise.all([
        apiJson(`/api/properties/${propertyId}`),
        apiJson(`/api/documents?property_id=${propertyId}`),
        apiJson(`/api/properties/${propertyId}/income`),
        apiJson(`/api/properties/${propertyId}/expenses`),
      ]);
      setProperty(prop);
      const sortedDocs = (Array.isArray(docs) ? docs : [])
        .filter(d => d.doc_type === 'manager_report')
        .sort((a, b) => new Date(b.period_month || 0) - new Date(a.period_month || 0));
      setDocuments(sortedDocs);
      setAllIncome((Array.isArray(inc) ? inc : []).map(r => ({ ...r, _editing: false })));
      setAllExpenses((Array.isArray(exp) ? exp : []).map(r => ({ ...r, _editing: false })));
      const parseYM = (val) => { const d = new Date(val); return isNaN(d) ? null : `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}`; };
      const mostRecentDoc = sortedDocs.length > 0 ? parseYM(sortedDocs[0].period_month) : null;
      const mostRecentIncome = (Array.isArray(inc) ? inc : []).map(r => parseYM(r.period_month)).filter(Boolean).sort().reverse()[0] || null;
      setSelectedMonth(mostRecentDoc || mostRecentIncome || '');
    } catch (err) {
      if (isUnauthorizedError(err)) redirectToLogin();
      else showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [propertyId, redirectToLogin]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedTableRows(new Set()); }, [activeTab, selectedMonth]);

  const toYM = (val) => {
    if (!val) return null;
    const d = new Date(val);
    if (isNaN(d)) return null;
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const allMonths = [...new Set([
    ...allIncome.map(r => toYM(r.period_month)).filter(Boolean),
    ...allExpenses.map(r => toYM(r.expense_date)).filter(Boolean),
    ...documents.map(d => toYM(d.period_month)).filter(Boolean),
  ])].sort().reverse();

  const filteredIncome = selectedMonth ? allIncome.filter(r => toYM(r.period_month) === selectedMonth) : allIncome;
  const filteredExpenses = selectedMonth ? allExpenses.filter(r => toYM(r.expense_date) === selectedMonth) : allExpenses;
  const selectedDoc = documents.find(d => toYM(d.period_month) === selectedMonth);
  const selectedDocPdfUrl = selectedDoc && String(selectedDocLink.docId) === String(selectedDoc.id) ? selectedDocLink.url : '';
  const selectedDocUrlLoading = selectedDoc && String(selectedDocLink.docId) === String(selectedDoc.id) ? selectedDocLink.loading : false;

  function getCachedDocumentUrl(docId) {
    const cached = documentUrls[docId];
    if (!cached) return null;
    if (cached.expiresAt && cached.expiresAt <= Date.now() + 30000) return null;
    return cached.url;
  }

  async function resolveDocumentUrl(doc, { silent = false } = {}) {
    if (!doc?.id) return doc?.url || null;
    const cachedUrl = getCachedDocumentUrl(doc.id);
    if (cachedUrl) return cachedUrl;
    try {
      const data = await apiJson(`/api/documents/${doc.id}/url`);
      const url = data?.url || doc.url || null;
      if (url) {
        const expiresMs = data?.expires_in ? Number(data.expires_in) * 1000 : 5 * 60 * 1000;
        setDocumentUrls(prev => ({ ...prev, [doc.id]: { url, expiresAt: Date.now() + expiresMs } }));
      }
      return url;
    } catch (err) {
      if (isUnauthorizedError(err)) { redirectToLogin(); return null; }
      if (!silent) showToast('Could not prepare a secure document link', 'warn');
      return doc.url || null;
    }
  }

  async function openDocument(doc) {
    const popup = window.open('', '_blank');
    if (popup) popup.opener = null;
    const url = await resolveDocumentUrl(doc);
    if (url && popup) popup.location.href = url;
    else if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else if (popup) popup.close();
  }

  async function downloadDocument(doc) {
    const url = await resolveDocumentUrl(doc);
    triggerDocumentDownload(url, doc?.filename || 'statement.pdf');
  }

  useEffect(() => {
    let cancelled = false;
    if (!selectedDoc) { setSelectedDocLink({ docId: null, url: '', loading: false }); return () => { cancelled = true; }; }
    const cachedUrl = getCachedDocumentUrl(selectedDoc.id);
    if (cachedUrl) { setSelectedDocLink({ docId: selectedDoc.id, url: cachedUrl, loading: false }); return () => { cancelled = true; }; }
    setSelectedDocLink({ docId: selectedDoc.id, url: '', loading: true });
    resolveDocumentUrl(selectedDoc, { silent: true })
      .then(url => { if (!cancelled) setSelectedDocLink({ docId: selectedDoc.id, url: url || selectedDoc.url || '', loading: false }); })
      .catch(() => { if (!cancelled) setSelectedDocLink({ docId: selectedDoc.id, url: selectedDoc.url || '', loading: false }); });
    return () => { cancelled = true; };
  }, [selectedDoc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRealExpenses = filteredExpenses.filter(r => r.category !== OWNER_DIST);
  const filteredOwnerDist = filteredExpenses.filter(r => r.category === OWNER_DIST);
  const monthTotalIncome = filteredIncome.filter(r => !r._new).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const monthTotalExpenses = filteredRealExpenses.filter(r => !r._new).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const monthTotalOwnerDist = filteredOwnerDist.filter(r => !r._new).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const monthNet = monthTotalIncome - monthTotalExpenses;

  async function handleAddRow(e) {
    if (e) e.preventDefault();
    if (!newRow.date) { showToast('Select a date/month', 'error'); return; }
    if (newRow.amount == null || Number.isNaN(parseFloat(newRow.amount))) { showToast('Enter a valid amount', 'error'); return; }
    const amount = parseFloat(newRow.amount);
    const nextMonth = newRow.date.slice(0, 7);
    try {
      if (newRow.row_type === 'income') {
        await apiJson(`/api/properties/${propertyId}/income`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ period_month: `${nextMonth}-01`, amount, category: newRow.category, notes: newRow.description }) });
      } else {
        await apiJson(`/api/properties/${propertyId}/expenses`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expense_date: newRow.date.slice(0, 10), amount, category: newRow.category, vendor: newRow.vendor, description: newRow.description }) });
      }
      showToast('Row added');
      setShowAddModal(false);
      resetNewRow(newRow.row_type);
      await load();
      if (nextMonth) setSelectedMonth(nextMonth);
    } catch (err) {
      if (isUnauthorizedError(err)) redirectToLogin();
      else showToast(err.message || 'Add failed', 'error');
    }
  }

  async function doBulkDelete() {
    if (!confirmBulkDelete?.rows?.length) return;
    const rowsToDelete = confirmBulkDelete.rows;
    setConfirmBulkDelete(null);
    const unsavedIncomeKeys = [];
    const unsavedExpenseKeys = [];
    const persistedRows = [];
    rowsToDelete.forEach((row) => {
      if (row?._raw?._new || row?._raw?.id == null) {
        if (row._type === 'income') unsavedIncomeKeys.push(getRowKey(row._raw));
        if (row._type === 'expense') unsavedExpenseKeys.push(getRowKey(row._raw));
        return;
      }
      persistedRows.push(row);
    });
    if (unsavedIncomeKeys.length > 0) setAllIncome(prev => prev.filter(e => !unsavedIncomeKeys.includes(getRowKey(e))));
    if (unsavedExpenseKeys.length > 0) setAllExpenses(prev => prev.filter(e => !unsavedExpenseKeys.includes(getRowKey(e))));
    let deleted = unsavedIncomeKeys.length + unsavedExpenseKeys.length;
    for (const row of persistedRows) {
      try {
        await apiJson(row._type === 'income' ? `/api/income/${row._raw.id}` : `/api/expenses/${row._raw.id}`, { method: 'DELETE' });
        deleted++;
      } catch (err) { if (isUnauthorizedError(err)) { redirectToLogin(); return; } }
    }
    setSelectedTableRows(new Set());
    if (persistedRows.length > 0) await load();
    showToast(`${deleted} row${deleted !== 1 ? 's' : ''} deleted`);
  }

  function resetNewRow(rowType = 'income') {
    setNewRow({ row_type: rowType, date: rowType === 'income' ? (selectedMonth || '') : (selectedMonth ? `${selectedMonth}-01` : ''), category: rowType === 'income' ? INCOME_CATS[0] : EXPENSE_CATS[0], amount: null, vendor: '', description: '' });
  }

  function openAddRow(rowType = 'income') { resetNewRow(rowType); setShowAddModal(true); }

  function updateDraftRow(rowData, field, value) {
    const sourceRow = rowData?._raw;
    if (!sourceRow) return;
    if (rowData.row_type === 'income') {
      setAllIncome(prev => prev.map(row => isSameRow(row, sourceRow) ? { ...row, period_month: field === 'date' ? value : row.period_month, category: field === 'category' ? value : row.category, amount: field === 'amount' ? value : row.amount, notes: field === 'description' ? value : row.notes } : row));
      return;
    }
    setAllExpenses(prev => prev.map(row => isSameRow(row, sourceRow) ? { ...row, expense_date: field === 'date' ? value : row.expense_date, category: field === 'category' ? value : row.category, amount: field === 'amount' ? value : row.amount, vendor: field === 'vendor' ? value : row.vendor, description: field === 'description' ? value : row.description } : row));
  }

  async function onCellEditComplete(e) {
    const { rowData, newValue, field } = e;
    const value = field === 'amount' ? Number(newValue) : newValue;
    if (value === rowData[field] || (field === 'amount' && Number(value) === Number(rowData[field]))) return;
    if (field === 'amount' && (newValue === null || newValue === '' || Number.isNaN(value))) { showToast('Enter a valid amount', 'error'); return; }
    if (rowData._new) { updateDraftRow(rowData, field, value); return; }
    try {
      await apiJson(`/api/document-rows/${rowData.row_type}/${rowData.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...rowData, [field]: value }) });
      updateDraftRow(rowData, field, value);
    } catch (err) {
      if (isUnauthorizedError(err)) redirectToLogin();
      else showToast('Save failed', 'error');
    }
  }

  const textEditor = (options) => <InputText value={options.value ?? ''} onChange={e => options.editorCallback(e.target.value)} style={{ width: '100%', fontSize: 13 }} />;
  const numberEditor = (options) => <InputNumber value={options.value === '' ? null : options.value} onValueChange={e => options.editorCallback(e.value)} mode="decimal" minFractionDigits={2} maxFractionDigits={2} style={{ width: '100%' }} />;
  const categoryEditor = (options) => {
    const cats = options.rowData.row_type === 'income' ? INCOME_CATS : EXPENSE_CATS;
    return <Dropdown value={options.value} options={cats.map(c => ({ label: fmtCat(c), value: c }))} onChange={e => options.editorCallback(e.value)} style={{ width: '100%', fontSize: 13 }} />;
  };

  if (loading) return <div className={embedded ? 'sr-panel-loading' : 'sr-page'}><PageLoader label="Loading statements" /></div>;

  const content = (
    <>
      {!externalToast && <Toast ref={localToast} position="top-right" />}

      {confirmBulkDelete && (
        <div className="sr-modal-backdrop">
          <div className="sr-modal">
            <p>Delete {confirmBulkDelete.rows.length} selected row{confirmBulkDelete.rows.length !== 1 ? 's' : ''}? This cannot be undone.</p>
            <div className="sr-modal-actions">
              <button className="sr-act sr-act-del" onClick={doBulkDelete}>Delete</button>
              <button className="sr-act sr-act-cancel" onClick={() => setConfirmBulkDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="docs-modal-backdrop docs-add-modal-backdrop">
          <div className="docs-add-modal">
            <div className="docs-add-modal-header">
              <div><p className="docs-add-modal-eyebrow">Manual Entry</p><h3>Add Statement Row</h3></div>
              <button className="docs-add-close" onClick={() => setShowAddModal(false)}><i className="pi pi-times" /></button>
            </div>
            <form className="docs-add-modal-form" onSubmit={handleAddRow}>
              <div className="docs-add-modal-grid">
                <label>
                  <span>Property</span>
                  <select value={String(propertyId)} disabled>
                    <option value={String(propertyId)}>{property?.nickname || property?.address_line1 || 'Property'}</option>
                  </select>
                </label>
                <label>
                  <span>Type</span>
                  <select value={newRow.row_type} onChange={e => { const t = e.target.value; setNewRow(r => ({ ...r, row_type: t, category: t === 'income' ? INCOME_CATS[0] : EXPENSE_CATS[0], date: t === 'income' ? (selectedMonth || r.date?.slice(0,7) || '') : (r.date?.length === 7 ? `${r.date}-01` : r.date || (selectedMonth ? `${selectedMonth}-01` : '')) })); }}>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </label>
                <label>
                  <span>{newRow.row_type === 'income' ? 'Month' : 'Date'}</span>
                  <input type={newRow.row_type === 'income' ? 'month' : 'date'} value={newRow.date} onChange={e => setNewRow(r => ({ ...r, date: e.target.value }))} />
                </label>
                <label>
                  <span>Category</span>
                  <select value={newRow.category} onChange={e => setNewRow(r => ({ ...r, category: e.target.value }))}>
                    {(newRow.row_type === 'income' ? INCOME_CATS : EXPENSE_CATS).map(c => <option key={c} value={c}>{fmtCat(c)}</option>)}
                  </select>
                </label>
                <label>
                  <span>Amount</span>
                  <InputNumber value={newRow.amount} onValueChange={e => setNewRow(r => ({ ...r, amount: e.value }))} mode="decimal" minFractionDigits={2} maxFractionDigits={2} inputClassName="docs-add-amount-input" placeholder="0.00" />
                </label>
                {newRow.row_type === 'expense' && (
                  <label><span>Vendor</span><InputText value={newRow.vendor || ''} onChange={e => setNewRow(r => ({ ...r, vendor: e.target.value }))} /></label>
                )}
                <label className="docs-add-span2">
                  <span>{newRow.row_type === 'income' ? 'Notes' : 'Description'}</span>
                  <InputText value={newRow.description || ''} onChange={e => setNewRow(r => ({ ...r, description: e.target.value }))} />
                </label>
              </div>
              <div className="docs-add-modal-actions">
                <button type="button" className="docs-add-reset" onClick={() => resetNewRow(newRow.row_type)}>Clear</button>
                <div style={{ flex: 1 }} />
                <button type="button" className="docs-add-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="docs-add-btn"><i className="pi pi-check" /> Save Row</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header — hidden when embedded (property detail has its own nav) */}
      {!embedded && (
        <div className="sr-header">
          <div className="sr-header-left">
            <button className="sr-back" onClick={() => navigate(`/properties/${propertyId}`)}>
              <i className="pi pi-arrow-left" /> {property?.nickname || property?.address_line1 || 'Property'}
            </button>
            <div>
              <p className="sr-eyebrow">Statement Review</p>
              <h1 className="sr-title">{property?.nickname || property?.address_line1}</h1>
            </div>
          </div>
          <div className="sr-header-right">
            <button className="sr-upload-btn" onClick={() => navigate('/documents')}>
              <i className="pi pi-upload" /> Upload Statement
            </button>
            <label className="sr-month-label">Viewing period</label>
            <select className="sr-month-select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
              <option value="">All time</option>
              {allMonths.map(m => {
                const hasDoc = documents.some(d => toYM(d.period_month) === m);
                return <option key={m} value={m}>{new Date(m + '-02').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}{hasDoc ? ' 📄' : ''}</option>;
              })}
            </select>
          </div>
        </div>
      )}

      {/* Embedded header — compact, sits inside the card */}
      {embedded && (
        <div className="sr-embedded-header">
          <div className="sr-embedded-header-left">
            <h2 className="sr-embedded-title">Statement Review</h2>
          </div>
          <div className="sr-embedded-header-right">
            <button className="sr-upload-btn" onClick={() => navigate('/documents')}>
              <i className="pi pi-upload" /> Upload Statement
            </button>
            <select className="sr-month-select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
              <option value="">All time</option>
              {allMonths.map(m => {
                const hasDoc = documents.some(d => toYM(d.period_month) === m);
                return <option key={m} value={m}>{new Date(m + '-02').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}{hasDoc ? ' 📄' : ''}</option>;
              })}
            </select>
          </div>
        </div>
      )}

      {/* Summary strip */}
      <div className="sr-summary">
        <div className="sr-sum-item"><span className="sr-sum-label">Income</span><span className="sr-sum-val sr-green">{fmt(monthTotalIncome)}</span></div>
        <div className="sr-sum-item"><span className="sr-sum-label">Expenses</span><span className="sr-sum-val sr-red">{fmt(monthTotalExpenses)}</span></div>
        <div className="sr-sum-item"><span className="sr-sum-label">Net</span><span className={`sr-sum-val ${monthNet >= 0 ? 'sr-green' : 'sr-red'}`}>{fmt(monthNet)}</span></div>
        {monthTotalOwnerDist > 0 && <div className="sr-sum-item"><span className="sr-sum-label">Owner Distribution</span><span className="sr-sum-val sr-owner">{fmt(monthTotalOwnerDist)}</span></div>}
        {selectedDoc && <div className="sr-sum-item sr-sum-item--doc"><i className="pi pi-file-pdf" /><span>{selectedDoc.filename}</span></div>}
      </div>

      {/* Two-column: PDF + Tables */}
      <div className={`sr-body ${selectedDoc ? 'sr-body--split' : 'sr-body--full'}`}>
        {selectedDoc && (
          <div className="sr-pdf-panel">
            <div className="sr-pdf-header">
              <span className="sr-pdf-title"><i className="pi pi-file-pdf" />{selectedDoc.filename}</span>
              <a href={selectedDocPdfUrl || selectedDoc.url || '#'} target="_blank" rel="noreferrer" className="sr-pdf-open"
                onClick={e => { e.preventDefault(); openDocument(selectedDoc); }}>
                <i className="pi pi-external-link" /> Open
              </a>
            </div>
            {selectedDocUrlLoading && <p className="docs-preview-loading">Preparing secure preview...</p>}
            <iframe className="sr-pdf-iframe" src={selectedDocPdfUrl || 'about:blank'} title="Statement PDF" />
          </div>
        )}

        <div className="sr-tables-panel">
          <div className="sr-tabs">
            <button className={`sr-tab ${activeTab === 'summary' ? 'sr-tab--active' : ''}`} onClick={() => setActiveTab('summary')}>Summary</button>
            <button className={`sr-tab ${activeTab === 'statement' ? 'sr-tab--active' : ''}`} onClick={() => setActiveTab('statement')}>Statement <span className="sr-tab-count">{filteredIncome.filter(r => !r._new).length + filteredRealExpenses.filter(r => !r._new).length}</span></button>
            <button className={`sr-tab ${activeTab === 'income' ? 'sr-tab--active' : ''}`} onClick={() => setActiveTab('income')}>Income <span className="sr-tab-count">{filteredIncome.filter(r => !r._new).length}</span></button>
            <button className={`sr-tab ${activeTab === 'expenses' ? 'sr-tab--active' : ''}`} onClick={() => setActiveTab('expenses')}>Expenses <span className="sr-tab-count">{filteredRealExpenses.filter(r => !r._new).length + filteredOwnerDist.filter(r => !r._new).length}</span></button>
            <button className={`sr-tab ${activeTab === 'documents' ? 'sr-tab--active' : ''}`} onClick={() => setActiveTab('documents')}>Documents <span className="sr-tab-count">{documents.length}</span></button>
          </div>

          {activeTab === 'summary' && (
            <div className="sr-table-section">
              <div className="sr-summary-grid">
                <div className="sr-summary-col">
                  <div className="sr-summary-col-header sr-summary-col-header--income"><span>Income</span><span className="sr-green">{fmt(monthTotalIncome)}</span></div>
                  {filteredIncome.filter(r => !r._new).length === 0 ? <p className="sr-empty">No income this period.</p> : (
                    <table className="sr-table">
                      <thead><tr><th>Month</th><th>Category</th><th>Amount</th><th>Notes</th></tr></thead>
                      <tbody>{filteredIncome.filter(r => !r._new).map(r => <tr key={r.id}><td>{r.period_month?.slice(0,7)}</td><td className="sr-cap">{r.category}</td><td className="sr-money sr-green">{fmt(r.amount)}</td><td className="sr-note">{r.notes||'—'}</td></tr>)}</tbody>
                      <tfoot><tr><td colSpan={2} className="sr-foot-label">Total</td><td className="sr-money sr-green sr-foot-val">{fmt(monthTotalIncome)}</td><td /></tr></tfoot>
                    </table>
                  )}
                </div>
                <div className="sr-summary-col">
                  <div className="sr-summary-col-header sr-summary-col-header--expenses"><span>Expenses</span><span className="sr-red">{fmt(monthTotalExpenses)}</span></div>
                  {filteredRealExpenses.filter(r => !r._new).length === 0 ? <p className="sr-empty">No expenses this period.</p> : (
                    <table className="sr-table">
                      <thead><tr><th>Date</th><th>Category</th><th>Amount</th><th>Vendor</th></tr></thead>
                      <tbody>{filteredRealExpenses.filter(r => !r._new).map(r => <tr key={r.id}><td>{r.expense_date?.slice(0,10)}</td><td className="sr-cap">{r.category}</td><td className="sr-money sr-red">{fmt(r.amount)}</td><td className="sr-note">{r.vendor||'—'}</td></tr>)}</tbody>
                      <tfoot><tr><td colSpan={2} className="sr-foot-label">Total</td><td className="sr-money sr-red sr-foot-val">{fmt(monthTotalExpenses)}</td><td /></tr></tfoot>
                    </table>
                  )}
                  {filteredOwnerDist.filter(r => !r._new).length > 0 && (
                    <>
                      <div className="sr-summary-col-header sr-summary-col-header--owner" style={{marginTop:16}}><span>Owner Distribution</span><span className="sr-owner">{fmt(monthTotalOwnerDist)}</span></div>
                      <table className="sr-table">
                        <thead><tr><th>Date</th><th>Amount</th><th>Description</th></tr></thead>
                        <tbody>{filteredOwnerDist.filter(r => !r._new).map(r => <tr key={r.id}><td>{r.expense_date?.slice(0,10)}</td><td className="sr-money sr-owner">{fmt(r.amount)}</td><td className="sr-note">{r.description||'—'}</td></tr>)}</tbody>
                        <tfoot><tr><td className="sr-foot-label">Total</td><td className="sr-money sr-owner sr-foot-val">{fmt(monthTotalOwnerDist)}</td><td /></tr></tfoot>
                      </table>
                    </>
                  )}
                </div>
              </div>
              <div className="sr-summary-net">
                <span className="sr-summary-net-label">Net Amount</span>
                <span className={`sr-summary-net-val ${monthNet >= 0 ? 'sr-green' : 'sr-red'}`}>{fmt(monthNet)}</span>
              </div>
            </div>
          )}

          {['statement', 'income', 'expenses'].includes(activeTab) && (() => {
            const stmtRows = [
              ...filteredIncome.filter(r => !r._new).map(r => ({ _id: r.id, _type: 'income', row_type: 'income', id: r.id, _raw: r, _new: false, date: toYM(r.period_month) || selectedMonth, category: r.category, amount: r.amount, vendor: '', description: r.notes || '' })),
              ...filteredRealExpenses.filter(r => !r._new).map(r => ({ _id: r.id, _type: 'expense', row_type: 'expense', id: r.id, _raw: r, _new: false, date: r.expense_date?.slice(0,10), category: r.category, amount: r.amount, vendor: r.vendor || '', description: r.description || '' })),
            ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

            function makeCsvExport(rows, filename) {
              const cell = v => `"${String(v ?? '').replace(/[\r\n]+/g, ' ').replace(/"/g, '""')}"`;
              const headers = ['Date', 'Type', 'Category', 'Amount', 'Vendor', 'Description'];
              const lines = rows.map(r => [cell(r.date), cell(r._type), cell(r.category), r.amount ?? '', cell(r.vendor), cell(r.description)].join(','));
              const a = document.createElement('a');
              a.href = URL.createObjectURL(new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' }));
              a.download = filename; a.click();
            }

            function makeCopy(rows) {
              const clean = v => String(v ?? '').replace(/[\r\n\t]+/g, ' ').trim();
              const headers = ['Date', 'Type', 'Category', 'Amount', 'Vendor', 'Description'];
              const lines = rows.map(r => [clean(r.date), clean(r._type), clean(r.category), r.amount ?? '', clean(r.vendor), clean(r.description)].join('\t'));
              navigator.clipboard.writeText([headers.join('\t'), ...lines].join('\n')).then(() => showToast('Copied to clipboard')).catch(() => showToast('Copy failed', 'error'));
            }

            function StyledTable({ rows, totalInc, totalExp, net, onAddIncome, onAddExpense, showAdd, csvFile }) {
              const selectedRows = rows.filter(row => { const k = getStatementTableRowKey(row); return k ? selectedTableRows.has(k) : false; });
              const allRowsSelected = rows.length > 0 && selectedRows.length === rows.length;
              const toggleRow = row => { const k = getStatementTableRowKey(row); if (!k) return; setSelectedTableRows(prev => { const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next; }); };
              const toggleAll = () => allRowsSelected ? setSelectedTableRows(new Set()) : setSelectedTableRows(new Set(rows.map(getStatementTableRowKey).filter(Boolean)));
              return (
                <div className="sr-table-section">
                  <div className="sr-stmt-totals">
                    <span className="sr-stmt-total-item"><span className="sr-stmt-total-label">Income</span><span className="sr-green sr-stmt-total-val">{fmt(totalInc)}</span></span>
                    <span className="sr-stmt-sep">−</span>
                    <span className="sr-stmt-total-item"><span className="sr-stmt-total-label">Expenses</span><span className="sr-red sr-stmt-total-val">{fmt(totalExp)}</span></span>
                    <span className="sr-stmt-sep">=</span>
                    <span className="sr-stmt-total-item"><span className="sr-stmt-total-label">Net</span><span className={`sr-stmt-total-val ${net >= 0 ? 'sr-green' : 'sr-red'}`}>{fmt(net)}</span></span>
                    <div className="sr-stmt-actions">
                      {showAdd === 'income' && <button className="sr-stmt-btn" onClick={onAddIncome}><i className="pi pi-plus" /> Add</button>}
                      {showAdd === 'expense' && <button className="sr-stmt-btn" onClick={onAddExpense}><i className="pi pi-plus" /> Add</button>}
                      {showAdd === 'both' && <><button className="sr-stmt-btn" onClick={onAddIncome}><i className="pi pi-plus" /> Income</button><button className="sr-stmt-btn" onClick={onAddExpense}><i className="pi pi-plus" /> Expense</button></>}
                      {selectedRows.length > 0 && <button className="sr-stmt-btn sr-stmt-btn--delete" onClick={() => setConfirmBulkDelete({ rows: selectedRows })}><i className="pi pi-trash" /> Delete ({selectedRows.length})</button>}
                      <button className="sr-stmt-btn" onClick={() => makeCsvExport(rows, csvFile)}><i className="pi pi-download" /> CSV</button>
                      <button className="sr-stmt-btn" onClick={() => makeCopy(rows)}><i className="pi pi-copy" /> Copy</button>
                    </div>
                  </div>
                  {rows.length === 0 ? <p className="sr-empty">No data for this period.</p> : (
                    <div className="sr-review-table-wrap">
                      <DataTable value={rows} editMode="cell" size="small" tableStyle={{ minWidth: '820px' }}
                        rowClassName={row => row._type === 'income' ? 'sr-stmt-row--income' : row.category === OWNER_DIST ? 'sr-stmt-row--owner' : 'sr-stmt-row--expense'}>
                        <Column header={<input type="checkbox" checked={allRowsSelected} onChange={toggleAll} />} style={{width:'42px'}} body={row => <input type="checkbox" checked={selectedTableRows.has(getStatementTableRowKey(row))} onChange={() => toggleRow(row)} onClick={e => e.stopPropagation()} />} />
                        <Column field="date" header="Date" style={{width:'115px', color:'#64748b', fontVariantNumeric:'tabular-nums'}} />
                        {activeTab === 'statement' && <Column field="row_type" header="Type" style={{width:'95px'}} body={row => <span className={`sr-type-pill sr-type-pill--${row._type}`}>{row._type === 'income' ? 'Income' : 'Expense'}</span>} />}
                        <Column field="category" header="Category" style={{width:'150px'}} editor={categoryEditor} onCellEditComplete={onCellEditComplete} body={row => fmtCat(row.category)} />
                        <Column field="amount" header="Amount" style={{width:'120px', textAlign:'right'}} editor={numberEditor} onCellEditComplete={onCellEditComplete} body={row => <span className={row.category === OWNER_DIST ? 'sr-owner' : row._type === 'income' ? 'sr-green' : 'sr-red'}>{fmt(row.amount)}</span>} />
                        <Column field="vendor" header="Vendor" style={{width:'150px'}} editor={textEditor} onCellEditComplete={onCellEditComplete} body={row => row.vendor || ''} />
                        <Column field="description" header="Description" editor={textEditor} onCellEditComplete={onCellEditComplete} body={row => row.description || ''} />
                      </DataTable>
                    </div>
                  )}
                </div>
              );
            }

            const incomeOnlyRows = stmtRows.filter(r => r._type === 'income');
            const expenseOnlyRows = [
              ...stmtRows.filter(r => r._type === 'expense'),
              ...filteredOwnerDist.filter(r => !r._new).map(r => ({ _id: r.id, _type: 'expense', row_type: 'expense', id: r.id, _raw: r, _new: false, date: r.expense_date?.slice(0,10), category: r.category, amount: r.amount, vendor: r.vendor || '', description: r.description || '' })),
            ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

            if (activeTab === 'statement') return <StyledTable rows={stmtRows} totalInc={monthTotalIncome} totalExp={monthTotalExpenses} net={monthNet} onAddIncome={() => openAddRow('income')} onAddExpense={() => openAddRow('expense')} showAdd="both" csvFile={`statement_${selectedMonth || 'export'}.csv`} />;
            if (activeTab === 'income') return <StyledTable rows={incomeOnlyRows} totalInc={monthTotalIncome} totalExp={0} net={monthTotalIncome} onAddIncome={() => openAddRow('income')} showAdd="income" csvFile={`income_${selectedMonth || 'export'}.csv`} />;
            if (activeTab === 'expenses') return <StyledTable rows={expenseOnlyRows} totalInc={0} totalExp={monthTotalExpenses} net={-monthTotalExpenses} onAddExpense={() => openAddRow('expense')} showAdd="expense" csvFile={`expenses_${selectedMonth || 'export'}.csv`} />;
            return null;
          })()}

          {activeTab === 'documents' && (
            <div className="sr-table-section">
              <div className="sr-table-header">
                <span className="sr-table-label">Uploaded Documents</span>
                <button className="sr-add-btn" onClick={() => navigate('/documents')}><i className="pi pi-upload" /> Upload</button>
              </div>
              {documents.length === 0 ? <p className="sr-empty">No documents uploaded for this property.</p> : (
                <div className="sr-table-wrap">
                  <table className="sr-table">
                    <thead><tr><th>File</th><th>Period</th><th>Uploaded</th><th>Parsed</th><th></th></tr></thead>
                    <tbody>
                      {documents.map(doc => (
                        <tr key={doc.id}>
                          <td><span style={{display:'flex',alignItems:'center',gap:6}}><i className="pi pi-file-pdf" style={{color:'#ef4444'}} />{doc.filename}</span></td>
                          <td>{toYM(doc.period_month) ? new Date(toYM(doc.period_month)+'-02').toLocaleDateString('en-US',{month:'short',year:'numeric'}) : '—'}</td>
                          <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}</td>
                          <td><span className={`sr-parsed-badge ${doc.parsed ? 'sr-parsed-badge--yes' : 'sr-parsed-badge--no'}`}>{doc.parsed ? 'Yes' : 'No'}</span></td>
                          <td><button type="button" className="sr-act sr-act-edit" onClick={() => openDocument(doc)}>Open</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Full Ledger */}
      <div className="sr-ledger-section">
        <div className="sr-ledger-title-row">
          <h2 className="sr-ledger-title">Full Ledger</h2>
          {ledgerExpanded && allMonths.length > 12 && (
            <button className="sr-ledger-expand-btn sr-ledger-expand-btn--inline" onClick={() => setLedgerExpanded(false)}>
              <i className="pi pi-chevron-up" /> Collapse
            </button>
          )}
        </div>
        <div className="sr-ledger-grid">
          {allMonths.length === 0 ? <p className="sr-empty">No records yet.</p> : (ledgerExpanded ? allMonths : allMonths.slice(0, 12)).map(month => {
            const mIncome = allIncome.filter(r => toYM(r.period_month) === month && !r._new);
            const mExpenses = allExpenses.filter(r => toYM(r.expense_date) === month && !r._new && r.category !== OWNER_DIST);
            const mDoc = documents.find(d => toYM(d.period_month) === month);
            const mNet = mIncome.reduce((s,r) => s+(parseFloat(r.amount)||0),0) - mExpenses.reduce((s,r) => s+(parseFloat(r.amount)||0),0);
            return (
              <div key={month} className={`sr-ledger-row ${month === selectedMonth ? 'sr-ledger-row--active' : ''}`} onClick={() => setSelectedMonth(month)}>
                <div className="sr-ledger-month">{new Date(month+'-02').toLocaleDateString('en-US',{month:'short',year:'numeric'})}</div>
                <div className="sr-ledger-stats"><span className="sr-green">{fmt(mIncome.reduce((s,r)=>s+(parseFloat(r.amount)||0),0))}</span><span className="sr-ledger-sep">—</span><span className="sr-red">{fmt(mExpenses.reduce((s,r)=>s+(parseFloat(r.amount)||0),0))}</span></div>
                <div className={`sr-ledger-net ${mNet >= 0 ? 'sr-green' : 'sr-red'}`}>{fmt(mNet)}</div>
                <div className="sr-ledger-doc">
                  {mDoc?.url ? (
                    <div className="sr-ledger-doc-actions" onClick={e => e.stopPropagation()}>
                      <button type="button" className="sr-doc-badge" title={`Open ${mDoc.filename}`} onClick={() => openDocument(mDoc)}><i className="pi pi-file-pdf" /> Statement</button>
                      <button type="button" className="sr-doc-download" onClick={() => downloadDocument(mDoc)} title={`Download ${mDoc.filename}`} aria-label={`Download ${mDoc.filename}`}><i className="pi pi-download" /></button>
                    </div>
                  ) : <span className="sr-no-doc">No PDF</span>}
                </div>
              </div>
            );
          })}
        </div>
        {allMonths.length > 12 && (
          <button className="sr-ledger-expand-btn" onClick={() => setLedgerExpanded(e => !e)}>
            {ledgerExpanded ? 'Collapse ledger' : `Show full ledger (${allMonths.length - 12} more)`}
          </button>
        )}
      </div>
    </>
  );

  if (embedded) return content;

  return <div className="sr-page"><Toast ref={localToast} position="top-right" />{content}</div>;
}

// ── Standalone page wrapper ───────────────────────────────────────────────────

export default function StatementReview() {
  const { id } = useParams();
  return <StatementReviewPanel propertyId={id} embedded={false} />;
}
